import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowRight, ArrowUpRight } from "lucide-react";
import { FaLinkedin, FaInstagram } from "react-icons/fa";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { DOCTOR } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const Contact = () => {
  const { t, lang } = useLang();
  return (
  <main data-testid="contact-page">
    <PageHero
      key={lang}
      eyebrow={t("ct_hero_eyebrow")}
      lines={[t("ct_hero_l1"), t("ct_hero_l2")]}
      testId="contact-hero"
    >
      {t("ct_hero_sub")}
    </PageHero>

    <section className="bg-bone pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Reveal>
          <a
            href={DOCTOR.phoneHref}
            data-testid="contact-call-card"
            className="group block bg-pine-950 text-bone p-10 lg:p-12 h-full relative overflow-hidden"
          >
            <Phone size={110} strokeWidth={0.8} className="absolute -right-4 -bottom-4 text-pine-800 transition-transform duration-700 group-hover:scale-110" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-300">
                {t("ct_call_label")}
              </div>
              <div className="mt-5 font-serif font-light text-4xl lg:text-5xl tracking-tight">
                {DOCTOR.phoneDisplay}
              </div>
              <div className="mt-5 space-y-1.5 text-sm text-pine-100/85">
                {DOCTOR.appointmentLines.map((l) => (
                  <a
                    key={l.number}
                    href={l.href}
                    data-testid={`contact-desk-line-${l.number.replace(/[^0-9]/g, "")}`}
                    className="block hover:text-white transition-colors duration-300"
                  >
                    {l.label}: <span className="font-semibold">{l.number}</span>
                  </a>
                ))}
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-pine-300 group-hover:text-white transition-colors duration-300">
                {t("ct_call_now")} <ArrowUpRight size={16} />
              </span>
            </div>
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <a
            href={DOCTOR.emailHref}
            data-testid="contact-email-card"
            className="group block bg-white border border-mist p-10 lg:p-12 h-full hover:border-pine-500/50 transition-colors duration-500 relative overflow-hidden"
          >
            <Mail size={110} strokeWidth={0.8} className="absolute -right-4 -bottom-4 text-pine-100 transition-transform duration-700 group-hover:scale-110" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-500">
                {t("ct_email_label")}
              </div>
              <div className="mt-5 font-serif font-light text-2xl lg:text-3xl tracking-tight text-pine-950 break-all">
                {DOCTOR.email}
              </div>
              <div className="font-serif font-light text-2xl lg:text-3xl tracking-tight text-pine-950 break-all">
                {DOCTOR.emailHospital}
              </div>
              <p className="mt-4 text-sm text-sage max-w-xs leading-relaxed">
                {t("ct_email_body")}
              </p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-pine-900 group-hover:text-pine-500 transition-colors duration-300">
                {t("ct_email_cta")} <ArrowUpRight size={16} />
              </span>
            </div>
          </a>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="contact-visit-card" className="bg-white border border-mist p-10 lg:p-12 h-full">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-500">
              {t("ct_visit_label")}
            </div>
            <div className="mt-5 flex items-start gap-4">
              <MapPin size={22} className="mt-1 shrink-0 text-pine-500" />
              <div>
                <div className="font-serif text-2xl text-pine-950 leading-snug">{DOCTOR.hospital}</div>
                <p className="mt-2 text-sm text-sage leading-relaxed">
                  Raja Ram Mohan Roy Road, Prathna Samaj, Khetwadi, Girgaon, Mumbai, Maharashtra — 400004
                </p>
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-mist flex flex-wrap gap-x-8 gap-y-3">
              <a
                href={DOCTOR.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-pine-900 hover:text-pine-500 transition-colors duration-300"
              >
                <FaLinkedin size={17} className="text-[#0A66C2]" />
                Dr. Lokesh Naik G on LinkedIn
              </a>
              <a
                href={DOCTOR.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-pine-900 hover:text-pine-500 transition-colors duration-300"
              >
                <span className="inline-flex w-5 h-5 rounded items-center justify-center text-white bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)]">
                  <FaInstagram size={13} />
                </span>
                Instagram
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div data-testid="contact-hours-card" className="bg-white border border-mist p-10 lg:p-12 h-full">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-500">
              {t("ct_hours_label")}
            </div>
            <div className="mt-5 flex items-start gap-4">
              <Clock size={22} className="mt-1 shrink-0 text-pine-500" />
              <div className="text-sm leading-relaxed w-full">
                <div className="space-y-2.5" data-testid="opd-schedule">
                  {[t("opd_mf"), t("opd_sat"), t("opd_sun")].map((day, i) => {
                    const h = DOCTOR.opdHours[i];
                    return (
                      <div
                        key={day}
                        className="flex items-center justify-between gap-6 border-b border-mist/70 pb-2.5"
                      >
                        <span className="font-semibold text-pine-950">{day}</span>
                        <span className={h.time === "Closed" ? "text-red-800/70 font-medium" : "text-sage"}>
                          {h.time === "Closed" ? t("opd_closed") : h.time}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-4 text-sage">
                  {t("ct_em", { phone: DOCTOR.phoneDisplay })}
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
          <div data-testid="contact-map-card" className="bg-white border border-mist overflow-hidden">
            <div className="p-8 lg:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-500">
                  {t("ct_map_label")}
                </div>
                <div className="mt-2 font-serif font-light text-2xl lg:text-3xl tracking-tight text-pine-950">
                  {DOCTOR.hospital}
                </div>
                <p className="mt-1.5 text-sm text-sage">
                  Raja Ram Mohan Roy Road, Prathna Samaj, Khetwadi, Girgaon, Mumbai, Maharashtra 400004
                </p>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Sir+H.+N.+Reliance+Foundation+Hospital+and+Research+Centre+Mumbai"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-link"
                className="shrink-0 inline-flex items-center gap-2 bg-pine text-bone text-sm font-semibold px-6 py-3.5 rounded-full hover:bg-pine-500 transition-colors duration-300"
              >
                {t("ct_directions")} <ArrowUpRight size={15} />
              </a>
            </div>
            <iframe
              title="Sir H. N. Reliance Foundation Hospital and Research Centre — Map"
              data-testid="hospital-map"
              src="https://maps.google.com/maps?q=Sir%20H.%20N.%20Reliance%20Foundation%20Hospital%20and%20Research%20Centre%2C%20Raja%20Ram%20Mohan%20Roy%20Rd%2C%20Girgaon%2C%20Mumbai%2C%20Maharashtra%20400004&t=&z=16&ie=UTF8&iwloc=&output=embed"
              className="w-full h-80 lg:h-[26rem] border-0"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
          <Link
            to="/book"
            data-testid="contact-book-button"
            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-pine-100/70 border border-pine-200 p-10 lg:p-12 hover:bg-pine-100 transition-colors duration-500"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-600">
                {t("ct_strip_label")}
              </div>
              <div className="mt-3 font-serif font-light text-3xl lg:text-4xl tracking-tight text-pine-950">
                {t("ct_strip_title")}
              </div>
            </div>
            <span className="inline-flex items-center gap-2 bg-pine text-bone font-semibold text-sm px-8 py-4 rounded-full group-hover:bg-pine-500 transition-colors duration-300 shrink-0">
              {t("ct_strip_btn")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </div>
      </Reveal>
    </section>
  </main>
  );
};

export default Contact;
